import { Alert } from "@package/subframe/components/Alert";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { DiscardEditsDialog } from "@/components/people/profile/DiscardEditsDialog";
import { ProfileBanner } from "@/components/people/profile/ProfileBanner";
import { ProfileDetailsTab } from "@/components/people/profile/ProfileDetailsTab";
import { ProfileOverviewTab } from "@/components/people/profile/ProfileOverviewTab";
import { ProfilePageHeader } from "@/components/people/profile/ProfilePageHeader";
import { ProfileSectionNav } from "@/components/people/profile/ProfileSectionNav";
import { ProfileStatusStrip } from "@/components/people/profile/ProfileStatusStrip";
import { AcknowledgementsSection } from "@/components/people/sections/AcknowledgementsSection";
import { LoginAndSecuritySection } from "@/components/people/sections/LoginAndSecuritySection";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useGetEmployeeProfile } from "@/generated/people/people";
import { ProfileEditingContext } from "@/hooks/use-profile-section-form";
import { fullName, isProfileUnavailable } from "@/lib/people";
import {
  availableProfileTabs,
  PROFILE_TAB_LABELS,
  PROFILE_TAB_PANEL_ID,
  type ProfileTab,
  type ProfileTabParam,
  profileTabParam,
  resolveProfileTab,
} from "@/lib/people-profile-tabs";
import { staffUsername } from "@/lib/staff-username";

interface EmployeeProfilePageProps {
  userId: string;
  /** From `?tab=`; Overview when absent. */
  tab: ProfileTabParam | undefined;
}

/** The tab lives in the URL, so it survives a reload and the back button. */
export function EmployeeProfilePage({
  userId,
  tab: requestedTab,
}: EmployeeProfilePageProps) {
  const navigate = useNavigate();
  const { selectedNurseryId, selectedNursery } = useNursery();
  const { user } = useAuth();
  // Switching tab unmounts the cards, so an open edit would be lost: ask first.
  const openEdits = useRef(0);
  const editingRegistry = useMemo(
    () => ({
      register: () => {
        openEdits.current += 1;
        return () => {
          openEdits.current -= 1;
        };
      },
    }),
    [],
  );
  const [pendingTab, setPendingTab] = useState<ProfileTab | null>(null);
  const profileQuery = useGetEmployeeProfile(selectedNurseryId ?? "", userId, {
    query: {
      enabled: !!selectedNurseryId,
      refetchOnMount: "always",
      // Never kept in memory after the page unmounts: a session can end
      // without a full reload, and the next person on the device must not see
      // this profile flash up from cache.
      gcTime: 0,
    },
  });
  const profile = profileQuery.data?.data;

  // A link to a tab the viewer doesn't have shows Overview; drop it from the URL too.
  useEffect(() => {
    if (!profile) return;
    if (!requestedTab) return;
    const available = availableProfileTabs({
      canViewLogin: profile.loginAndSecurity !== null,
    });
    if (available.includes(requestedTab)) return;
    void navigate({
      to: "/people/$userId",
      params: { userId },
      search: { tab: undefined },
      replace: true,
    });
  }, [profile, requestedTab, navigate, userId]);

  if (!selectedNurseryId) {
    return (
      <PageShell>
        <Alert
          variant="neutral"
          title="Select a nursery to view this profile"
        />
      </PageShell>
    );
  }

  // Offline the query pauses rather than failing, so neither isLoading nor
  // isError is set. Profiles are never cached for offline use.
  if (profileQuery.isPaused && !profile) {
    return (
      <PageShell>
        <Alert
          variant="neutral"
          title="Profiles need a connection"
          description="Reconnect to view this profile."
        />
      </PageShell>
    );
  }

  if (profileQuery.isError) {
    return (
      <PageShell>
        {isProfileUnavailable(profileQuery.error) ? (
          <Alert
            variant="neutral"
            title={`This profile isn't available at ${selectedNursery?.name ?? "this nursery"}`}
          />
        ) : (
          <Alert
            variant="error"
            title="Could not load this profile"
            description="Try again in a moment."
          />
        )}
      </PageShell>
    );
  }

  if (!profile) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const isOwnProfile = user?.id === userId;
  const tabs = availableProfileTabs({
    canViewLogin: profile.loginAndSecurity !== null,
  });
  const tab = resolveProfileTab(requestedTab, tabs);
  const goToTab = (next: ProfileTab) =>
    void navigate({
      to: "/people/$userId",
      params: { userId },
      search: { tab: profileTabParam(next) },
    });
  const setTab = (next: ProfileTab) => {
    if (next === tab) return;
    if (openEdits.current > 0) {
      setPendingTab(next);
      return;
    }
    goToTab(next);
  };

  return (
    <ProfileEditingContext.Provider value={editingRegistry}>
      <PageShell testId="employee-profile">
        <ProfilePageHeader
          isOwnProfile={isOwnProfile}
          canViewDirectory={profile.capabilities.canViewDirectory}
        />
        <ProfileBanner
          profile={profile}
          isOwnProfile={isOwnProfile}
          onOpenAcknowledgements={() => setTab("acknowledgements")}
        />
        {!profile.hasRecordedDetails && (
          <Alert
            variant="neutral"
            title="No employee details recorded yet"
            description="Only account details are shown until someone adds more."
          />
        )}
        <ProfileStatusStrip
          keyInformation={profile.keyInformation}
          loginAndSecurity={profile.loginAndSecurity}
        />
        <ProfileSectionNav
          tabs={tabs}
          current={tab}
          onChange={setTab}
          outstandingCount={profile.acknowledgements.outstandingCount}
        />
        <div
          role="tabpanel"
          id={PROFILE_TAB_PANEL_ID}
          aria-label={PROFILE_TAB_LABELS[tab]}
          tabIndex={0}
          className="flex w-full flex-col gap-6 outline-none"
        >
          {tab === "overview" && (
            <ProfileOverviewTab
              nurseryId={selectedNurseryId}
              userId={userId}
              profile={profile}
            />
          )}
          {tab === "details" && (
            <ProfileDetailsTab
              nurseryId={selectedNurseryId}
              userId={userId}
              profile={profile}
            />
          )}
          {tab === "acknowledgements" && (
            <AcknowledgementsSection
              key={userId}
              nurseryId={selectedNurseryId}
              userId={userId}
              isOwnProfile={isOwnProfile}
              canSendReminders={profile.capabilities.canSendReminders}
            />
          )}
          {tab === "login" && profile.loginAndSecurity && (
            <LoginAndSecuritySection
              nurseryId={selectedNurseryId}
              userId={userId}
              displayName={
                fullName(
                  profile.keyInformation.firstName,
                  profile.keyInformation.lastName,
                ) || staffUsername(profile.keyInformation.email)
              }
              email={profile.keyInformation.email}
              nurseryRole={profile.keyInformation.nurseryRole}
              loginAndSecurity={profile.loginAndSecurity}
              canManageLogin={profile.capabilities.canManageLogin}
              pinLoginEnabled={selectedNursery?.pinLoginEnabled ?? false}
            />
          )}
        </div>
        <DiscardEditsDialog
          open={pendingTab !== null}
          onKeepEditing={() => setPendingTab(null)}
          onDiscard={() => {
            if (pendingTab) goToTab(pendingTab);
            setPendingTab(null);
          }}
        />
      </PageShell>
    </ProfileEditingContext.Provider>
  );
}

function PageShell({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex w-full max-w-6xl flex-col gap-6 px-4 py-6"
    >
      {children}
    </div>
  );
}
